import moment from "moment";
import React from "react";
import {
  DEFAULT_DATE_FORMATE,
  DEFAULT_DATE_TIME_FORMATE,
  DEFAULT_TIME_FORMATE,
  getDuration,
  isAdmin,
} from "./global";
import { calculatePaymentAmount, getJobTitle } from "../Util/jobUtil";
import { Badge, Button } from "react-bootstrap";
import { PencilFill, Trash } from "react-bootstrap-icons";
import useAuth from "../hooks/useAuth";
import { ADMIN_ROLE } from "./constants";

export const userTimeLogTableColumns = ({
  onDeleteTimeLogClick,
  onEditLogClick,
  totalCost,
  users,
  findIfLogIsSelected,
  onToggleSelectAll,
  onToggleSelectLog,
  selectAllMode,
}) => {
  const { user } = useAuth();

  const columns = [
    {
      key: "view",
      labelRenderer: (field) => (
        <input
          type="checkbox"
          className="hover"
          checked={selectAllMode}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelectAll(e.target.checked);
          }}
        />
      ),
      cellRenderer: (log) => (
        <div>
          <input
            type="checkbox"
            className="hover"
            checked={findIfLogIsSelected(log)}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelectLog(log);
            }}
          />
        </div>
      ),
      onCellClick: (log) => onToggleSelectLog(log),
      disableSort: true,
      disableSearch: true,
      width: 50,
    },
    {
      key: "user",
      label: "User",
      width: 150,
      hide: user?.role !== ADMIN_ROLE,
      valueSelector: (log) => log?.user?.name,
      searchOptions: users
        ?.filter((u) => u.isEnabled)
        .map((u) => ({ label: u?.name, value: u?._id })),
    },
    {
      key: "entryOrigin",
      label: "Origin",
      width: 100,
      cellRenderer: (log) => (
        <Badge variant="primary">{log?.entryOrigin}</Badge>
      ),
      searchOptions: ["Clocked", "Manually Added"]?.map((u) => ({
        label: u,
        value: u,
      })),
    },
    {
      key: "status",
      label: "Status",
      width: 100,
      cellRenderer: (log) => (
        <Badge variant={log?.status === "Approved" ? "success" : "danger"}>
          {log?.status}
        </Badge>
      ),
      searchOptions: ["Approved", "Unapproved"]?.map((u) => ({
        label: u,
        value: u,
      })),
      exactMatch: true,
    },
    {
      key: "startDateTime",
      label: "Time",
      type: "date",
      width: 60,
      cellRenderer: (log) => (
        <>
          {moment(log?.endDateTime).format(DEFAULT_DATE_FORMATE)}
          {", "}
          {moment(log?.startDateTime).format(DEFAULT_TIME_FORMATE)} -{" "}
          {log?.endDateTime
            ? moment(log?.endDateTime).format(DEFAULT_TIME_FORMATE)
            : "Running"}
        </>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      width: 60,
      cellRenderer: (log) => (
        <>
          {getDuration({
            startDateTime: log?.startDateTime,
            endDateTime: log?.endDateTime,
          })}
        </>
      ),
      disableSearch: true,
    },

    {
      key: "comment",
      label: "Comment",
      type: "text",
      width: 250,
      truncate: true,
      disableSearch: true,
    },
    {
      key: "job",
      label: "Job",
      width: 60,
      cellRenderer: (log) => <> {log.job ? getJobTitle(log.job) : "-"}</>,
      disableSearch: true,
    },
    {
      key: "cost",
      label: "Cost",
      type: "number",
      hide: user?.role !== ADMIN_ROLE,
      width: 60,
      disableSearch: true,
      cellRenderer: (log) => (
        <>{`$${calculatePaymentAmount(
          log?.startDateTime,
          log?.endDateTime,
          log.user.hourlyWage
        )?.toFixed(2) || 0}`}</>
      ),
    },

    // {
    //   key: "costPercentage",
    //   label: "Progress",
    //   type: "number",
    //   width: 60,
    //   disableSearch: true,
    //   cellRenderer: (log) => {
    //     const cost = calculatePaymentAmount(
    //       log?.startDateTime,
    //       log?.endDateTime,
    //       log?.user?.hourlyWage
    //     );
    //     const percentage = totalCost
    //       ? ((cost / totalCost) * 100).toFixed(2)
    //       : 0;
    //     return <>{`${percentage}%`}</>;
    //   },
    // },

    {
      key: "Action",
      label: "Action",
      width: 60,
      disableSearch: true,
      cellRenderer: (log) => (
        <div className="tiny-text d-flex justify-content-center gap-2">
          {(isAdmin(user?.role) ||
            (log?.status === "Unapproved" &&
              log?.entryOrigin === "Manually Added")) && (
            <Button
              className="d-flex align-items-center px-1"
              variant="outline-danger"
              size="sm"
              onClick={() => onDeleteTimeLogClick(log)}
            >
              <Trash />
            </Button>
          )}
          {(isAdmin(user?.role) ||
            (log?.status === "Unapproved" &&
              log?.entryOrigin === "Manually Added")) && (
            <Button
              className="d-flex align-items-center px-1"
              variant="outline-success "
              size="sm"
              onClick={() => onEditLogClick(log)}
            >
              <PencilFill />
            </Button>
          )}
          {!isAdmin(user?.role) &&
            log?.status === "Unapproved" &&
            log?.entryOrigin === "Clocked" && (
              <Button
                className="d-flex align-items-center px-1"
                variant="outline-success"
                size="sm"
                onClick={() => onEditLogClick(log)}
              >
                <PencilFill />
              </Button>
            )}
        </div>
      ),
    },
  ].filter((t) => !t.hide);

  return columns;
};

export const getJobStatTableColumns = () => {
  const { user } = useAuth();
  const columns = [
    {
      key: "jobDesc",
      label: "Job Description",
      width: 60,
      cellRenderer: (data) => <>{getJobTitle(data.job)}</>,
    },

    {
      key: "user",
      label: "User",
      width: 60,
      cellRenderer: (data) => <>{data?.users?.length}</>,
    },

    {
      key: "hours",
      label: "Working hours",
      width: 60,
      cellRenderer: (data) => (
        <>{`${data?.users
          ?.reduce((a, v) => a + Number(v?.hours), 0)
          .toFixed(2) || 0}`}</>
      ),
    },
    {
      key: "cost",
      label: "Cost",
      width: 60,
      hide: user?.role !== ADMIN_ROLE,
      cellRenderer: (data) => (
        <>{`$${data?.users
          ?.reduce((a, v) => a + Number(v?.cost), 0)
          ?.toFixed(2) || 0}`}</>
      ),
    },
  ].filter((t) => !t.hide);
  return columns;
};

export const getUserStatTableColumns = (totalCost) => {
  const { user } = useAuth();
  const columns = [
    {
      key: "name",
      label: "Name",
      width: 60,
      cellRenderer: (data) => <>{data?.user?.name}</>,
    },

    {
      key: "email",
      label: "Email",
      type: "number",
      width: 60,
      cellRenderer: (data) => <>{data?.user?.email}</>,
    },
    {
      key: "job",
      label: "Job",
      width: 60,
      cellRenderer: (data) => <> {data?.jobs?.length}</>,
    },
    {
      key: "hours",
      label: "Working hours",
      width: 60,
      cellRenderer: (data) => (
        <>{`${data?.jobs
          ?.reduce((a, v) => a + Number(v?.hours), 0)
          .toFixed(2) || 0}`}</>
      ),
    },
    {
      key: "cost",
      label: "Cost",
      width: 60,
      hide: user?.role !== ADMIN_ROLE,
      cellRenderer: (data) => (
        <>{`$${data?.jobs
          ?.reduce((a, v) => a + Number(v?.cost), 0)
          ?.toFixed(2) || 0}`}</>
      ),
    },
  ].filter((t) => !t.hide);
  return columns;
};
